import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundary, { recordGlobalError } from "./components/ErrorBoundary.jsx";
import App from "./App.jsx";
import "./index.css";

// ── Global error instrumentation ─────────────────────────────────────────────
// Captures errors that escape React boundaries (async callbacks, socket
// handlers, setTimeout/Promise chains). Records them to sessionStorage and
// `window.__twErrors` so remote debugging is possible without DevTools.
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    recordGlobalError("window.error", event.error || new Error(event.message), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason));
    recordGlobalError("unhandledrejection", err, {
      reason: typeof reason === "object" ? safeStringify(reason) : String(reason),
    });
  });
}

function safeStringify(value) {
  try {
    return JSON.stringify(value, Object.getOwnPropertyNames(value));
  } catch {
    return String(value);
  }
}

// DevTools only in development — excluded from production bundle
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null;

// React Query Client com configurações otimizadas
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Defaults conservadores para a maioria dos recursos
      staleTime: 1000 * 30, // 30 segundos
      gcTime: 1000 * 60 * 10, // 10 minutos no cache
      refetchOnWindowFocus: false, // Evita refetches desnecessários em tab switch
      retry: (failureCount, error) => {
        // Nunca retenta 401 (não autenticado) nem 429 (rate limit)
        if (error?.status === 401 || error?.status === 429) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
      <Suspense fallback={null}>
        <ReactQueryDevtools initialIsOpen={false} />
      </Suspense>
    </QueryClientProvider>
  </React.StrictMode>,
);
